import React, { useState } from 'react';
import styled from 'styled-components';
import Button from '@mui/material/Button';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import usePost from '../../../../../Hooks/usePost';
import { useProfile } from '../../../../../Contexts/UserProfileContext';

const StyledSaveButton = styled(Button)`
    background: #2e69fe 0% 0% no-repeat padding-box !important;
    box-shadow: 0px 3px 3px #00000061;
    border-radius: 4px;
    opacity: 1;

    &.MuiButton-contained.Mui-disabled {
        color: #fafbf7;
    }
    :disabled {
        background: #9b9b9d 0% 0% no-repeat padding-box !important;
    }
`;
const ChangePasswordApplet = styled.div`
    margin-top: 40px;
    margin-left: auto;
    margin-right: auto;
    height: 600px;
    width: 700px;
    text-align: center;
    padding: 32px;
    background-color: #fafbf7;
    box-shadow: 2px 2px 2px #00000059;
    border-radius: 4px;
    opacity: 1;
`;

const ChangePasswordContainer = styled.div`
    display: flex;
    height: 900px;
    width: 800px;
    margin-left: auto;
    margin-right: auto;
    flex-direction: column;
`;

const Label = styled.div`
    letter-spacing: 0px;
    color: #000000;
    text-align: left;
`;

const StyledValue = styled.span`
    letter-spacing: 0px;
    color: #000000;
    padding-left: 8px;
    font-weight: bold;
`;

const Title = styled.div`
    font-size: 23px;
    font-weight: bold;
    margin-top: 80px;
    letter-spacing: 0px;
    color: #000000;
    opacity: 1;
    width: 700px;
    margin-left: 50px;
    text-align: center;
`;

const InputContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const StyledTextField = styled(TextField)`
    width: 250px;
`;
const ChangePassword = () => {
    const { profile } = useProfile();

    const { userName, userId } = { ...profile };
    const { post } = usePost('/changePassword');

    const [accountData, setAccountData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: '',
        showCurrentPassword: false,
        showNewPassword: false,
        showConfirmNewPassword: false,
    });

    const initialPostDataValue = {
        message: [],
        error: false,
        success: false,
        isLoading: false,
    };
    const [postData, setPostData] = useState(initialPostDataValue);

    const {
        currentPassword,
        newPassword,
        confirmNewPassword,
        showCurrentPassword,
        showConfirmNewPassword,
        showNewPassword,
    } = { ...accountData };

    const handleInputValidation = (error) => {
        if (error.code === 'auth/wrong-password') {
            setPostData({ message: ['Password is incorrect'], input: 'currentPassword' });
        }
        if (error.code === 'auth/weak-password') {
            setPostData({
                message:
                    ['Password must be at least 8 characters and include 1 letter and 1 number.'],
                input: 'newPassword',
            });
        }
        if (newPassword !== confirmNewPassword) {
            setPostData({
                ...error,
                message: ['Passwords do not match'],
                input: 'confirmNewPassword',
            });
        }
    };

    const handleChange = (prop) => (event) => {
        setPostData({ ...initialPostDataValue });
        setAccountData({ ...accountData, [prop]: event.target.value, [prop]: event.target.value });
    };

    const changePassword = async () => {
        try {
            setPostData({ ...postData, isLoading: true });
            const changePassResp = await post(
                {
                    userName,
                    userId,
                    currentPassword,
                    newPassword,
                },
                'POST'
            );

            if (changePassResp.data.result === false) {
                setPostData({
                    ...postData,
                    success: false,
                    isLoading: false,
                    error: true,
                    message: changePassResp.data.errors,
                });
            } else {
                setPostData({
                    ...postData,
                    success: true,
                    isLoading: false,
                    error: false,
                    message: ['Password changed succesfully.'],
                });
            }
        } catch (error) {
            setPostData({
                ...postData,
                error: true,
                message: ['Something went wrong, Please try again later'],
                data: null,
                isLoading: false,
            });
            handleInputValidation(error);
        }
    };

    const saveButtonisDisabled =
        !currentPassword ||
        !userName ||
        !currentPassword ||
        !newPassword ||
        !confirmNewPassword ||
        currentPassword.length < 6 ||
        currentPassword.length > 100 ||
        newPassword.length < 6 ||
        newPassword.length > 100 ||
        newPassword !== confirmNewPassword;
    return (
        <ChangePasswordContainer>
            <Title>Change Password</Title>
            <ChangePasswordApplet>
                <Label>
                    Email:
                    <StyledValue>{userName}</StyledValue>
                </Label>
                <br />

                <InputContainer>
                    <Label>For your security, enter your current password:</Label>
                    <br />
                    <StyledTextField
                        id="current-password"
                        label="Current Password"
                        type={showCurrentPassword ? 'text' : 'password'}
                        defaultValue={currentPassword}
                        onChange={handleChange('currentPassword')}
                        variant="outlined"
                        error={currentPassword.length < 6 || currentPassword.length > 100}
                        helperText={
                            currentPassword.length < 6 || currentPassword.length > 100
                                ? 'Password must be between 6 and 100 characters.'
                                : ''
                        }
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() =>
                                            setAccountData({
                                                ...accountData,
                                                showCurrentPassword: !showCurrentPassword,
                                            })
                                        }
                                        onMouseDown={() =>
                                            setAccountData({
                                                ...accountData,
                                                showCurrentPassword: !showCurrentPassword,
                                            })
                                        }
                                    >
                                        {showCurrentPassword ? <Visibility /> : <VisibilityOff />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <br />
                    <Label>Change Password:</Label>
                    <br />
                    <StyledTextField
                        id="new-password"
                        label="New Password"
                        type={showNewPassword ? 'text' : 'password'}
                        defaultValue={newPassword}
                        onChange={handleChange('newPassword')}
                        variant="outlined"
                        error={newPassword.length < 6 || newPassword.length > 100}
                        helperText={
                            newPassword.length < 6 || newPassword.length > 100
                                ? 'Password must be between 6 and 100 characters'
                                : ''
                        }
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() =>
                                            setAccountData({
                                                ...accountData,
                                                showNewPassword: !showNewPassword,
                                            })
                                        }
                                        onMouseDown={() =>
                                            setAccountData({
                                                ...accountData,
                                                showNewPassword: !showNewPassword,
                                            })
                                        }
                                    >
                                        {showNewPassword ? <Visibility /> : <VisibilityOff />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <br />
                    <StyledTextField
                        id="confirm-new-password"
                        label="Confirm New Password"
                        type={showConfirmNewPassword ? 'text' : 'password'}
                        defaultValue={confirmNewPassword}
                        onChange={handleChange('confirmNewPassword')}
                        variant="outlined"
                        error={newPassword !== confirmNewPassword}
                        helperText={
                            newPassword !== confirmNewPassword ? 'Passwords do not match.' : ''
                        }
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() =>
                                            setAccountData({
                                                ...accountData,
                                                showConfirmNewPassword: !showConfirmNewPassword,
                                            })
                                        }
                                        onMouseDown={() =>
                                            setAccountData({
                                                ...accountData,
                                                showConfirmNewPassword: !showConfirmNewPassword,
                                            })
                                        }
                                    >
                                        {showConfirmNewPassword ? (
                                            <Visibility />
                                        ) : (
                                            <VisibilityOff />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Label
                        style={{
                            marginTop: '8px',
                            marginBottom: '8px',
                            color: postData.error ? 'red' : 'green',
                        }}
                    >
                        {postData.message.length > 1
                            ? postData.message.map((item, idx) => <div key={idx}>{item}</div>)
                            : postData.message}
                    </Label>

                    <Label style={{ fontStyle: 'italic' }}>Password Criteria:</Label>
                    <br />
                    <Label style={{ fontStyle: 'italic', marginTop: '-23px' }}>
                        8-20 characters using 1 letter and 1 number.
                    </Label>
                </InputContainer>
                <br />

                <StyledSaveButton
                    disabled={saveButtonisDisabled}
                    onClick={() => changePassword()}
                    variant="contained"
                    color="primary"
                >
                    {postData.isLoading ? (
                        <CircularProgress
                            style={{ height: '20px', width: '20px', color: 'white' }}
                            color="primary"
                        />
                    ) : (
                        'Save'
                    )}
                </StyledSaveButton>
                <div style={{ marginTop: '8px', color: 'green' }}>
                    {postData.data ? postData.data : null}
                </div>
            </ChangePasswordApplet>
        </ChangePasswordContainer>
    );
};

ChangePassword.propTypes = {
    // companyData: propTypes.array,
    // companyDataError: propTypes.shape({}),
};

ChangePassword.defaultProps = {
    // companyData: [],
    // companyDataError: {},
};

export default ChangePassword;
